import React, { useState, useRef } from "react"
import { manageProfileLogo } from "../../../api/manage_profile_logo"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../LoadingContext"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"

interface UploadLogoModalProps {
  image: string | ""
  onClose: (url: string) => void // モーダルを閉じるためのコールバック
}

const UploadLogoModal: React.FC<UploadLogoModalProps> = ({ image, onClose }) => {
  const { startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    imageName: "",
    imageData: ""
  })

  const [errors, setErrors] = useState({
    imageName: ""
  })

  const fileInputRef = useRef<HTMLInputElement>(null)
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const allowedExtensions = ["png", "jpg", "jpeg", "bmp"]
    const maxSize = 1 * 1024 * 1024 // 1MB
    const maxWidth = 1024
    const maxHeight = 1024

    // **拡張子チェック**
    const fileExtension = file.name.split(".").pop()?.toLowerCase()
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      setFormData({ imageName: "", imageData: "" })
      setErrors({ imageName: "png, jpg, bmp のみアップロード可能です。" })
      return
    }

    // **サイズチェック**
    if (file.size > maxSize) {
      setFormData({ imageName: "", imageData: "" })
      setErrors({ imageName: "1MB 以下のファイルを選択してください。" })
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      const image = new Image()
      image.onload = () => {
        // **解像度チェック**
        if (image.width > maxWidth || image.height > maxHeight) {
          setFormData({ imageName: "", imageData: "" })
          setErrors({ imageName: "画像のサイズは 1024×1024 以下にしてください。" })
          return
        }

        // **バリデーションOKなら状態更新**
        setFormData((prev) => ({
          ...prev,
          imageData: event.target?.result as string, // BASE64データ
          imageName: file.name // ファイル名
        }))
        setErrors({ imageName: "" }) // エラーリセット
      }
      image.src = event.target?.result as string
    }

    reader.readAsDataURL(file)
  }

  const validate = () => {
    const validationErrors = { ...errors }
    let hasError = false

    if (!formData.imageName) {
      validationErrors.imageName = "画像を選択してください"
      hasError = true
    } else {
      validationErrors.imageName = ""
    }

    if (!formData.imageData) {
      validationErrors.imageName = "画像データが無効です"
      hasError = true
    } else {
      validationErrors.imageName = ""
    }

    setErrors(validationErrors)
    return !hasError
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (errors.imageName != "") {
      return
    }

    if (validate()) {
      // バリデーション成功時の処理
      try {
        startLoading()
        const result = await manageProfileLogo(formData.imageName, formData.imageData)
        //console.log(result);
        onClose(result.logo) // 成功時にモーダルを閉じる
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login") // セッション切れ時にトップページへリダイレクト
        } else if (error.response) {
          const errorDetail = error.response.data.detail
          const validationErrors = { ...errors }
          validationErrors.imageName = errorDetail.message
          setErrors(validationErrors)
        } else {
          const validationErrors = { ...errors }
          validationErrors.imageName = "サーバーへの接続に失敗しました"
          setErrors(validationErrors)
        }
      } finally {
        stopLoading()
      }
    }
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <button className="close-button" onClick={() => onClose("")}>
          ×
        </button>
        <MDTypography variant="h4" fontWeight="medium">
          ロゴ画像のアップロード
        </MDTypography>
        <MDBox style={{ height: "20px" }}></MDBox>
        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          {image && image !== "" && (
            <MDBox
              component="img"
              src={image}
              alt="Brand"
              sx={{ display: "block", margin: "auto", maxWidth: "100%" }}
            />
          )}
          <MDBox alignItems="center" mt={4} mb={2} sx={{ width: "100%", textAlign: "center" }}>
            <MDButton
              variant="contained"
              color="info"
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                },
                width: "50%"
              }}
              onClick={handleClick}
            >
              画像を選択
            </MDButton>
          </MDBox>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }} // ボタンのみ表示し、実際のinputは非表示
          />
          {formData.imageData && (
            <MDBox textAlign="center" mt={2}>
              <MDTypography variant="body2" color="textSecondary">
                選択された画像名: {formData.imageName}
              </MDTypography>
              <MDBox
                component="img"
                src={formData.imageData}
                alt="Preview"
                sx={{
                  maxWidth: "100%",
                  maxHeight: "200px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  mt: 1
                }}
              />
            </MDBox>
          )}
          {errors.imageName && (
            <MDBox
              color="#FF0000" // テキスト色
              textAlign="center"
              mt={2}
              fontSize="0.7em" // フォントサイズ
            >
              {errors.imageName}
            </MDBox>
          )}
          <MDBox alignItems="center" mt={4} mb={2} sx={{ width: "100%", textAlign: "center" }}>
            <MDButton
              variant="contained"
              color="info"
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                },
                width: "50%"
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              更新
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </div>
  )
}

export default UploadLogoModal
